// Unified Analytics System for Google Tag Manager

import { ANALYTICS_EVENTS } from '../helpers/analytics_events_helper';
import { LoanCycle } from '../helpers/loan_cycle_helper';
import { ProductType } from '../types/product';

export interface AnalyticsEvent {
  event: string;
  user_id?: string;
  product_type?: ProductType;
  product_amount?: number;
  product_length?: number;
  product_apr?: number;
  product_state?: string;
  application_number?: number;
  has_open_loan?: boolean;
  is_logged_in?: boolean;
  funded_count?: number;
  entry_point?: string;
  flow_context?: string;
  validation_errors?: string[];
  error_message?: string;
  experiment_id?: string;
  experiment_variant?: string;
  // Allow any additional custom properties
  [key: string]: any;
}

const EVENT_DEBOUNCE_MS = 1000;
const eventTimestamps = new Map<string, number>();

class Analytics {
  private isEnabled(): boolean {
    return (
      process.env.REACT_APP_ANALYTICS_ENABLED === "true" &&
      typeof window !== "undefined" &&
      Array.isArray((window as any).dataLayer)
    );
  }

  private log(eventName: string, data: any): void {
    if (process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test") {
      console.log("📊", eventName, data);
    }
  }

  private isValidEvent(eventName: string): boolean {
    return Object.values(ANALYTICS_EVENTS).includes(eventName as any);
  }

  private shouldDebounce(eventName: string): boolean {
    const now = Date.now();
    const lastFired = eventTimestamps.get(eventName);
    if (lastFired && (now - lastFired) <= EVENT_DEBOUNCE_MS) {
      return true;
    }
    eventTimestamps.set(eventName, now);
    return false;
  }

  private storeData: any = null;

  // Set store data from outside (called by hooks/components)
  setStoreData(storeData: any): void {
    this.storeData = storeData;
  }

  private getContextFromStore(): Partial<AnalyticsEvent> {
    if (!this.storeData) return {};

    const currentUser = this.storeData.session && this.storeData.session.currentUser;
    const application = this.storeData.entities && this.storeData.entities.application && this.storeData.entities.application.details;
    const location = this.storeData.info && this.storeData.info.location;

    return {
      // User context
      user_id: currentUser && currentUser.id ? currentUser.id.toString() : undefined,
      is_logged_in: !!currentUser,
      application_number: currentUser && currentUser.applicationNumber ? currentUser.applicationNumber + 1 : undefined,
      has_open_loan: currentUser && currentUser.activeLoanCycle ? 
        LoanCycle.ACTIVE.includes(currentUser.activeLoanCycle) : undefined,
      funded_count: currentUser && currentUser.fundedCount,

      // Product context
      product_type: application && application.product,
      product_amount: application && application.amount,
      product_length: application && application.length,
      product_apr: application && application.apr,
      product_state: (application && application.state) || location,
    };
  }

  // Main tracking method
  track(eventName: ANALYTICS_EVENTS, customData: Partial<AnalyticsEvent> = {}): void {
    if (!this.isValidEvent(eventName)) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Invalid analytics event:", eventName);
      }
      return;
    }

    if (this.shouldDebounce(eventName)) {
      if (process.env.NODE_ENV === "development") {
        console.log("📊 Debounced event:", eventName);
      }
      return;
    }

    const contextData = this.getContextFromStore();
    const eventData: AnalyticsEvent = {
      event: eventName,
      ...contextData,
      ...customData,
    };

    // Always log in development
    this.log(eventName, eventData);

    // Push to GTM if enabled
    if (this.isEnabled()) {
      try {
        (window as any).dataLayer.push(eventData);
      } catch (error) {
        console.error("Analytics error:", error);
      }
    }
  }

  // Convenience methods
  trackLoad(eventName: ANALYTICS_EVENTS, customData?: Partial<AnalyticsEvent>): void {
    this.track(eventName, customData);
  }

  trackSubmit(eventName: ANALYTICS_EVENTS, customData?: Partial<AnalyticsEvent>): void {
    this.track(eventName, customData);
  }

  trackSuccess(eventName: ANALYTICS_EVENTS, customData?: Partial<AnalyticsEvent>): void {
    this.track(eventName, customData);
  }

  trackError(eventName: ANALYTICS_EVENTS, error: string | string[], customData?: Partial<AnalyticsEvent>): void {
    this.track(eventName, {
      ...customData,
      error_message: Array.isArray(error) ? error[0] : error,
    });
  }

  trackValidationError(eventName: ANALYTICS_EVENTS, errors: string[], customData?: Partial<AnalyticsEvent>): void {
    this.track(eventName, {
      ...customData,
      validation_errors: errors,
    });
  }

  trackExperiment(experimentId: string, variant: string, customData?: Partial<AnalyticsEvent>): void {
    this.track(ANALYTICS_EVENTS.AB_TEST_ASSIGNMENT, {
      ...customData,
      experiment_id: experimentId,
      experiment_variant: variant,
    });
  }
}

// Export singleton instance
export const analytics = new Analytics();

// Default export for easier importing
export default analytics;